
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function GrowthDealershipApplication() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    experience: '',
    location: '',
    targetMarket: '',
    investmentTimeline: '',
    fundingSource: '',
    businessPlan: '',
    marketingExperience: '',
    teamSize: '',
    currentRevenue: '',
    expansionPlans: '',
    competitorAnalysis: '',
    marketingBudget: '',
    operationalExperience: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d439c9pha0trr1ha9i8g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessName: '',
          businessType: '',
          experience: '',
          location: '',
          targetMarket: '',
          investmentTimeline: '',
          fundingSource: '',
          businessPlan: '',
          marketingExperience: '',
          teamSize: '',
          currentRevenue: '',
          expansionPlans: '',
          competitorAnalysis: '',
          marketingBudget: '',
          operationalExperience: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/dealership" className="inline-flex items-center text-blue-100 hover:text-white mb-6">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Dealership
          </Link>
          <div className="flex items-center mb-4">
            <h1 className="text-4xl font-bold">Growth Dealership Application</h1>
            <span className="ml-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">Most Popular</span>
          </div>
          <p className="text-xl text-blue-100">Investment: $100,000 | Perfect for expanding businesses</p>
        </div>
      </div>

      {/* Application Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Growth Package Details</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">What's Included:</h3>
              <ul className="text-blue-700 space-y-1">
                <li>• 4 Geo Domes + 2 Safari Tents + 1 Tiny Home</li>
                <li>• Advanced POS & Booking System</li>
                <li>• Complete Marketing Package</li>
                <li>• 4-Week Training Program</li>
                <li>• Territory Rights (100-mile radius)</li>
                <li>• Solar Power Systems</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="growth-dealership-form">
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Business Type *
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Business Type</option>
                    <option value="hospitality">Hospitality Industry</option>
                    <option value="real-estate">Real Estate</option>
                    <option value="tourism">Tourism & Travel</option>
                    <option value="retail">Retail Business</option>
                    <option value="franchise">Franchise Owner</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Business Experience *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Experience</option>
                    <option value="2-5">2-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10-15">10-15 years</option>
                    <option value="15+">15+ years</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="currentRevenue" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Annual Revenue *
                  </label>
                  <select
                    id="currentRevenue"
                    name="currentRevenue"
                    value={formData.currentRevenue}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Revenue Range</option>
                    <option value="under-100k">Under $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k-1m">$500,000 - $1,000,000</option>
                    <option value="1m+">$1,000,000+</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Territory Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="City, State/Province, Country"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Investment Details */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Investment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="investmentTimeline" className="block text-sm font-medium text-gray-700 mb-2">
                    Investment Timeline *
                  </label>
                  <select
                    id="investmentTimeline"
                    name="investmentTimeline"
                    value={formData.investmentTimeline}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Timeline</option>
                    <option value="immediate">Immediate (1-30 days)</option>
                    <option value="1-3-months">1-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="6-12-months">6-12 months</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="fundingSource" className="block text-sm font-medium text-gray-700 mb-2">
                    Funding Source *
                  </label>
                  <select
                    id="fundingSource"
                    name="fundingSource"
                    value={formData.fundingSource}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Funding Source</option>
                    <option value="personal-savings">Personal Savings</option>
                    <option value="business-loan">Business Loan</option>
                    <option value="investor">Investor Partnership</option>
                    <option value="business-equity">Business Equity</option>
                    <option value="combination">Combination</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="marketingBudget" className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Marketing Budget *
                  </label>
                  <select
                    id="marketingBudget"
                    name="marketingBudget"
                    value={formData.marketingBudget}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Budget Range</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k+">$100,000+</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="operationalExperience" className="block text-sm font-medium text-gray-700 mb-2">
                    Operational Management Experience *
                  </label>
                  <select
                    id="operationalExperience"
                    name="operationalExperience"
                    value={formData.operationalExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                  >
                    <option value="">Select Experience Level</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Business Strategy */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Strategy & Goals</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="targetMarket" className="block text-sm font-medium text-gray-700 mb-2">
                    Target Market Analysis *
                  </label>
                  <textarea
                    id="targetMarket"
                    name="targetMarket"
                    value={formData.targetMarket}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your target customers, demographics, and market analysis..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.targetMarket.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="businessPlan" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Plan & Growth Strategy *
                  </label>
                  <textarea
                    id="businessPlan"
                    name="businessPlan"
                    value={formData.businessPlan}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your business plan, growth strategy, and 3-year goals..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.businessPlan.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="expansionPlans" className="block text-sm font-medium text-gray-700 mb-2">
                    Expansion Plans *
                  </label>
                  <textarea
                    id="expansionPlans"
                    name="expansionPlans"
                    value={formData.expansionPlans}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your plans for expanding the dealership business..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.expansionPlans.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="competitorAnalysis" className="block text-sm font-medium text-gray-700 mb-2">
                    Competitor Analysis *
                  </label>
                  <textarea
                    id="competitorAnalysis"
                    name="competitorAnalysis"
                    value={formData.competitorAnalysis}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your local competition and how you plan to differentiate..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.competitorAnalysis.length}/500 characters</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="marketingExperience" className="block text-sm font-medium text-gray-700 mb-2">
                      Marketing Experience *
                    </label>
                    <select
                      id="marketingExperience"
                      name="marketingExperience"
                      value={formData.marketingExperience}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    >
                      <option value="">Select Experience Level</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="professional">Professional</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Team Size *
                    </label>
                    <select
                      id="teamSize"
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                    >
                      <option value="">Select Team Size</option>
                      <option value="2-3">2-3 people</option>
                      <option value="4-6">4-6 people</option>
                      <option value="7-10">7-10 people</option>
                      <option value="10+">10+ people</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Any additional information you'd like to share..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Growth Dealership Application'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 mr-2"></i>
                  <p className="text-green-800">Application submitted successfully! We'll contact you within 24 hours.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <p className="text-red-800">There was an error submitting your application. Please try again.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
